/**
 * AssetManagementDialog
 * Full-screen (90%×90%) dialog for managing editor assets.
 * 3 tabs: Backgrounds, Cliparts, Masks.
 *
 * For ADMIN users:
 *   - Sub-tabs: "Uploaded" (own brand) / "Library" (all superuser materials)
 *   - Library has Visible / Hidden toggles with multi-select hide/enable actions
 *
 * PERFORMANCE: Per-view state is cached so switching tabs does NOT re-fetch.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Spinner, Pagination } from "react-bootstrap";
import { FiSearch, FiPlus, FiImage, FiTrash2, FiEdit2, FiEye, FiEyeOff, FiCheck, FiX } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import styled from "styled-components";
import { apiPost, apiDelete } from "../../library/utils/common-services/apiCall";
import { ENDPOINTS } from "../../library/utils/constants/apiurl";
import AddAssetDialog from "./AddAssetDialog";
import ViewAssetDialog from "./ViewAssetDialog";
import { getUserDetails } from "../../library/utils/services/theme";
import ConfirmationDialog from "../../components/popups/ConfirmationDialog";
import { USER_TYPES, EDITOR_ASSETS } from "../../library/utils/constants";
import { decompressFromBase64 } from "../../library/utils/common-functions";
import { invalidateLayoutsCache, isAutoLayout } from "../../library/utils/services/layoutsCache";
import LayoutImgPlaceholder from "../layouts/LayoutsImgPlaceholder";
import LayoutText from "../layouts/LayoutsText";
import { useDispatch } from "react-redux";
import { refreshAssets } from "../../store/slices/appAlice";

// ─── Config ──────────────────────────────────────────────────────────────────

const ASSET_TABS = [
  { key: "background", label: "Backgrounds", singular: "Background" },
  { key: "clipart", label: "Cliparts", singular: "Clipart" },
  { key: "mask", label: "Masks", singular: "Mask" },
  { key: "layout", label: "Layouts", singular: "Layout" },
];

const ITEMS_PER_PAGE = 50;

const createTabCache = () => ({
  assets: [],
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  searchText: "",
  loaded: false,
});

// Build a composite cache key for a specific view
const getCacheKey = (type, subView, libraryFilter) => {
  if (subView === "library") return `${type}_library_${libraryFilter}`;
  return `${type}_uploaded`;
};

// ─── Styled Components ──────────────────────────────────────────────────────

const DialogOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 11100;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: amFadeIn 0.2s ease;
  @keyframes amFadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const DialogContainer = styled.div`
  width: 90%;
  height: 90%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: amSlideUp 0.3s ease;
  @keyframes amSlideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @media (max-width: 768px) { width: 95%; height: 95%; }
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
`;

const DialogTitle = styled.div`
  h2 { font-size: 1.35rem; font-weight: 700; color: #111827; margin: 0; }
  p { font-size: 0.82rem; color: #6b7280; margin: 4px 0 0 0; }
`;

const CloseButton = styled.button`
  background: none; border: none; padding: 6px; border-radius: 8px;
  cursor: pointer; color: #6b7280; transition: all 0.2s;
  &:hover { background: #f3f4f6; color: #111827; }
`;

const TabBar = styled.div`
  display: flex; gap: 0; padding: 0 24px;
  border-bottom: 2px solid #f0f0f0; flex-shrink: 0;
`;

const TabBtn = styled.button`
  padding: 12px 20px; border: none; background: none;
  font-size: 0.88rem; font-weight: 600;
  color: ${(p) => (p.$active ? "var(--primary, #4084B5)" : "#6b7280")};
  border-bottom: 2px solid ${(p) => (p.$active ? "var(--primary, #4084B5)" : "transparent")};
  margin-bottom: -2px; cursor: pointer; transition: all 0.2s; white-space: nowrap;
  &:hover { color: var(--primary, #4084B5); background: rgba(64, 132, 181, 0.04); }
`;

// Segmented control for Uploaded / Library (Admin only)
const SegmentedControl = styled.div`
  display: inline-flex; border-radius: 10px;
  overflow: hidden; flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #dee2e6;
`;

const SegmentBtn = styled.button`
  padding: 8px 20px; border: none; font-size: 0.82rem; font-weight: 600;
  cursor: pointer; transition: all 0.2s; letter-spacing: 0.01em;
  background: ${p => p.$active ? "var(--primary, #4084B5)" : "#fff"};
  color: ${p => p.$active ? "#fff" : "#6b7280"};
  &:hover:not([disabled]) {
    background: ${p => p.$active ? "var(--primary, #4084B5)" : "#f0f4f8"};
    color: ${p => p.$active ? "#fff" : "#374151"};
  }
`;

// Toggle for Visible / Hidden inside Library
const ToggleGroup = styled.div`
  display: inline-flex; border-radius: 10px;
  overflow: hidden; flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #dee2e6;
`;

const ToggleBtn = styled.button`
  padding: 8px 18px; border: none; font-size: 0.8rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; gap: 6px;
  background: ${p => p.$active
    ? (p.$variant === "hidden" ? "#d97706" : "#059669")
    : "#fff"};
  color: ${p => p.$active ? "#fff" : "#6b7280"};
  &:hover:not([disabled]) {
    background: ${p => p.$active
      ? (p.$variant === "hidden" ? "#b45309" : "#047857")
      : "#f0f4f8"};
    color: ${p => p.$active ? "#fff" : "#374151"};
  }
`;

const Divider = styled.div`
  width: 1px; height: 28px; background: #dee2e6; flex-shrink: 0;
`;

// Row that holds the segmented controls below main tabs
const SubControlRow = styled.div`
  display: flex; align-items: center; gap: 14px;
  padding: 12px 24px; border-bottom: 1px solid #edf0f3;
  flex-shrink: 0; background: #f8f9fb; flex-wrap: wrap;
`;

const ToolbarRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 14px 24px; border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0; flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  position: relative; flex: 1; min-width: 200px;
  svg {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%); color: #9ca3af; pointer-events: none;
  }
  input {
    padding-left: 36px; border-radius: 8px; border: 1px solid #e5e7eb;
    height: 38px; font-size: 0.85rem; width: 100%; transition: border-color 0.2s;
    &:focus {
      border-color: var(--primary, #4084B5);
      box-shadow: 0 0 0 3px rgba(64, 132, 181, 0.1); outline: none;
    }
  }
`;

const AddButton = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 18px; background: var(--primary, #4084B5);
  color: #fff; border: none; border-radius: 8px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: all 0.2s; white-space: nowrap;
  &:hover {
    background: var(--primary-dark, #356a94);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(64, 132, 181, 0.3);
  }
`;

const ActionButton = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: all 0.2s; white-space: nowrap; border: none;
  background: ${p => p.$variant === "danger" ? "#ef4444" : "var(--primary, #4084B5)"};
  color: #fff;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const DialogBody = styled.div`
  flex: 1; overflow-y: auto; padding: 0;
`;

const AssetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px; padding: 20px 24px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 8px; padding: 12px 16px;
  }
`;

const AssetCard = styled.div`
  position: relative; border-radius: 10px; overflow: hidden;
  background: #f8f9fa; border: 2px solid ${p => p.$selected ? "var(--primary, #4084B5)" : "#e9ecef"};
  cursor: ${p => p.$selectable ? "pointer" : "default"}; transition: all 0.2s; aspect-ratio: 1;
  &:hover {
    border-color: var(--primary, #4084B5);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  img { width: 100%; height: 100%; object-fit: contain; display: block; }
`;

const SelectCheckbox = styled.div`
  position: absolute; top: 8px; right: 8px; z-index: 10;
  width: 22px; height: 22px; border-radius: 6px;
  border: 2px solid ${p => p.$checked ? "var(--primary, #4084B5)" : "rgba(255,255,255,0.8)"};
  background: ${p => p.$checked ? "var(--primary, #4084B5)" : "rgba(0,0,0,0.3)"};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
  color: #fff;
`;

const AssetName = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: 6px 8px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: #fff; font-size: 0.7rem; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const DeleteBtn = styled.button`
  position: absolute; top: 8px; right: 8px;
  background: rgba(0, 0, 0, 0.6); color: #fff; border: none;
  border-radius: 50%; width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; z-index: 10; transition: all 0.2s;
  &:hover { background: #ef4444; transform: scale(1.1); }
`;

const EditBtn = styled.button`
  position: absolute; top: 8px; right: 44px;
  background: rgba(0, 0, 0, 0.6); color: #fff; border: none;
  border-radius: 50%; width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; z-index: 10; transition: all 0.2s;
  &:hover { background: var(--primary, #4084B5); transform: scale(1.1); }
`;

const NoImagePlaceholder = styled.div`
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #d1d5db;
`;

const LayoutPreview = styled.div`
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: #fff; padding: 6px;
  svg { width: 100%; height: 100%; }
`;

// Overlay pill, NOT a row below the card: AssetCard is aspect-ratio 1 with
// overflow hidden, so anything in normal flow after the preview is clipped away.
// Sits above the AssetName gradient band at the bottom.
const AutoLayoutToggle = styled.label`
  position: absolute; left: 8px; bottom: 30px; z-index: 10;
  display: flex; align-items: center; gap: 5px; margin: 0;
  padding: 3px 8px 3px 5px; border-radius: 999px;
  background: ${p => p.$on ? "var(--primary, #4084B5)" : "rgba(0, 0, 0, 0.55)"};
  color: #fff; font-size: 0.66rem; font-weight: 600; line-height: 1;
  cursor: pointer; user-select: none; transition: background 0.2s;
  opacity: ${p => p.$on ? 1 : 0.85};
  input { width: 12px; height: 12px; margin: 0; cursor: pointer; accent-color: #fff; }
  &:hover { opacity: 1; }
`;

const FilterSelect = styled.select`
  height: 38px; border-radius: 8px; border: 1px solid #e5e7eb;
  padding: 0 10px; font-size: 0.82rem; color: #374151; background: #fff;
  cursor: pointer; flex-shrink: 0;
  &:focus {
    border-color: var(--primary, #4084B5);
    box-shadow: 0 0 0 3px rgba(64, 132, 181, 0.1); outline: none;
  }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 60px 24px; text-align: center;
  .icon-wrapper {
    width: 56px; height: 56px; border-radius: 50%;
    background: #f3f4f6; display: flex; align-items: center;
    justify-content: center; margin-bottom: 16px; color: #9ca3af;
  }
  h3 { font-size: 0.95rem; font-weight: 600; color: #111827; margin: 0 0 4px 0; }
  p { font-size: 0.82rem; color: #6b7280; margin: 0 0 16px 0; }
`;

const LoadingWrapper = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 60px 24px; gap: 12px;
  color: #6b7280; font-size: 0.85rem;
`;

const FooterRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 24px; border-top: 1px solid #e9ecef;
  flex-shrink: 0; flex-wrap: wrap; gap: 12px;
`;

const FooterInfo = styled.span`
  font-size: 0.78rem; color: #9ca3af;
`;

const PaginationWrapper = styled.div`
  .pagination {
    margin: 0;
    .page-item .page-link { font-size: 0.78rem; padding: 4px 10px; }
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────

const getAssetThumbnail = (item) => {
  if (item.urls?.length > 0) {
    const small = item.urls.find((u) => u.size === "small");
    if (small) return small.url;
    const medium = item.urls.find((u) => u.size === "medium");
    if (medium) return medium.url;
    return item.urls[0]?.url;
  }
  return item.image || null;
};

// Layouts have no thumbnail URL — they store the layout objects in `layout_c`.
// Render an SVG preview the same way the Layouts sidebar tab does, but size the
// viewBox from the layout's OWN stored side dimensions (decoupled from the
// editor's canvasSize) so it fits whatever the current canvas is.
const renderLayoutPreview = (item) => {
  const sides = item?.layout_c?.layout;
  if (!Array.isArray(sides) || sides.length === 0) return null;
  const first = sides[0] || {};
  const isSpread = item.spread || item.number_of_layouts === 2 || sides.length === 2;
  const vbW = (Number(first.width) || 0) * (isSpread ? 2 : 1);
  const vbH = Number(first.height) || 0;
  if (!vbW || !vbH) return null;
  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", background: "#fff" }}
    >
      {sides.map((side, si) =>
        (side.objects || []).map((obj, oi) => (
          <g
            key={`${si}-${obj.id}-${oi}`}
            className={obj.type === "img" ? "inset-0 overflow-hidden" : ""}
          >
            <g
              style={{
                transform: `translate(${obj.transform.x}px, ${obj.transform.y}px) rotate(${obj.transform.rotation}deg)`,
                transformOrigin: "center center",
                transformBox: "fill-box",
              }}
            >
              {obj.type === "img" && <LayoutImgPlaceholder item={obj} />}
              {obj.type === "text" && <LayoutText item={obj} />}
            </g>
          </g>
        ))
      )}
    </svg>
  );
};

function AssetManagementDialog({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("background");
  const [subView, setSubView] = useState("uploaded"); // "uploaded" | "library"
  const [libraryFilter, setLibraryFilter] = useState("visible"); // "visible" | "hidden"
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [viewAssetId, setViewAssetId] = useState(null);
  const [addedTypes, setAddedTypes] = useState({
    background: false,
    clipart: false,
    mask: false,
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    show: false,
    assetId: null,
    loading: false,
  });

  // Library multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  // Layout tab filters — type (page/spread) and exact image count
  const [layoutTypeFilter, setLayoutTypeFilter] = useState("all"); // all | single | spread
  const [layoutImageFilter, setLayoutImageFilter] = useState("all"); // all | "<n>"

  const user = getUserDetails();
  const isAdmin = user?.userTypeCode === USER_TYPES.ADMIN;
  const isSuperUser = user?.userTypeCode === USER_TYPES.SUPERUSER;
  const isLayoutTab = activeTab === "layout";

  // A layout is manageable (deletable) by the super-user, or by the admin that
  // owns it (same brand). Other-brand layouts in the Library are view-only.
  const canManageItem = useCallback(
    (item) =>
      isSuperUser || (isAdmin && item?.brand_id && item.brand_id === user?.brand_id),
    [isSuperUser, isAdmin, user?.brand_id]
  );

  // Per-view cache — composite keys like "background_uploaded", "background_library_visible"
  const tabCacheRef = useRef({});
  const [, setRenderTick] = useState(0);
  const forceRender = () => setRenderTick((t) => t + 1);

  const currentCacheKey = getCacheKey(activeTab, subView, libraryFilter);

  // Ensure cache entry exists
  const getCache = useCallback((key) => {
    if (!tabCacheRef.current[key]) {
      tabCacheRef.current[key] = createTabCache();
    }
    return tabCacheRef.current[key];
  }, []);

  const cache = getCache(currentCacheKey);
  const activeTabConfig = ASSET_TABS.find((t) => t.key === activeTab);

  // ── Fetch assets ─────────────────────────────────────────────────────────
  const fetchAssets = useCallback(
    (cacheKey, type, page = 1, search = "", opts = {}) => {
      setIsLoading(true);

      // ── Layouts: separate endpoint + filter shape, and the response items
      // carry a compressed `layout_c` that must be decompressed for the SVG
      // preview. Uploaded → own brand; Library → all layouts (no brand filter).
      if (type === "layout") {
        const filter = {
          status: { $in: [1, 3] },
          asset_type: EDITOR_ASSETS.LAYOUT,
          ...(search.trim() && { search: search.trim() }),
        };
        if (opts.subView !== "library" && user?.brand_id) {
          filter.brand_id = user.brand_id;
        }
        // Type filter — single page vs 2-page spread
        if (opts.layoutType === "single") {
          filter.number_of_layouts = 1;
          filter.spread = false;
        } else if (opts.layoutType === "spread") {
          filter.number_of_layouts = 2;
          filter.spread = true;
        }
        // Exact photo-count filter
        if (opts.layoutImages && opts.layoutImages !== "all") {
          filter.number_of_images = Number(opts.layoutImages);
        }
        const data = {
          filter,
          skip: (page - 1) * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          sortField: "_id",
          sortOrder: "desc",
        };
        apiPost(ENDPOINTS.getLayouts, data)
          .then((response) => {
            const tc = getCache(cacheKey);
            if (response?.items) {
              tc.assets = response.items.map((item) => ({
                ...item,
                layout_c: decompressFromBase64(item.layout_c),
              }));
              tc.totalCount = response.totalCount || 0;
              tc.totalPages = Math.ceil(tc.totalCount / ITEMS_PER_PAGE) || 1;
            } else {
              tc.assets = [];
              tc.totalCount = 0;
              tc.totalPages = 1;
            }
            tc.currentPage = page;
            tc.searchText = search;
            tc.loaded = true;
            forceRender();
          })
          .catch((err) => {
            console.error("Error fetching layout assets:", err);
            const tc = getCache(cacheKey);
            tc.assets = [];
            tc.totalCount = 0;
            tc.totalPages = 1;
            tc.loaded = true;
            forceRender();
          })
          .finally(() => setIsLoading(false));
        return;
      }

      const filter = {
        status: 1,
        type,
        ...(search.trim() && { search: search.trim() }),
      };

      if (opts.subView === "library") {
        // Library → do NOT send brand_id (fetches all superuser materials)
        // Hidden sub-tab → additionally send is_hide_from_brand + brand_id
        if (opts.libraryFilter === "hidden") {
          filter.is_hide_from_brand = true;
          if (user?.brand_id) filter.brand_id = user.brand_id;
        }
        else {
          filter.is_visible_from_brand = true;
        }
      } else {
        // Uploaded → filter by this admin/user's brand
        if (user?.brand_id) filter.brand_id = user.brand_id;
      }

      const data = {
        filter,
        skip: (page - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      };
      // Skip auto brand_id injection for Library Visible (no brand filter at all)
      const skipBrandId = opts.subView === "library" && opts.libraryFilter !== "hidden";

      apiPost(ENDPOINTS.getEditorTypeWiseList, data,
        // { skipBrandId }
      )
        .then((response) => {
          const tc = getCache(cacheKey);
          if (response?.items) {
            tc.assets = response.items;
            tc.totalCount = response.totalCount || 0;
            tc.totalPages = Math.ceil(tc.totalCount / ITEMS_PER_PAGE) || 1;
          } else {
            tc.assets = [];
            tc.totalCount = 0;
            tc.totalPages = 1;
          }
          tc.currentPage = page;
          tc.searchText = search;
          tc.loaded = true;
          forceRender();
        })
        .catch((err) => {
          console.error(`Error fetching ${type} assets:`, err);
          const tc = getCache(cacheKey);
          tc.assets = [];
          tc.totalCount = 0;
          tc.totalPages = 1;
          tc.loaded = true;
          forceRender();
        })
        .finally(() => setIsLoading(false));
    },
    [getCache, user?.brand_id]
  );

  // Helper to fetch current view
  const fetchCurrentView = useCallback(
    (page = 1, search = "") => {
      const key = getCacheKey(activeTab, subView, libraryFilter);
      fetchAssets(key, activeTab, page, search, {
        subView,
        libraryFilter,
        layoutType: layoutTypeFilter,
        layoutImages: layoutImageFilter,
      });
    },
    [activeTab, subView, libraryFilter, layoutTypeFilter, layoutImageFilter, fetchAssets]
  );

  // On first visit of any view — load if not cached
  useEffect(() => {
    if (!cache.loaded) {
      fetchCurrentView(1, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCacheKey]);

  // Refetch the layout tab whenever its type/image-count filters change.
  // (These aren't part of the cache key, so drive the refetch explicitly.)
  // The initial mount fires with the default "background" tab, so the
  // isLayoutTab guard skips it — no double-fetch with the cache-key effect.
  useEffect(() => {
    if (!isLayoutTab) return;
    setSelectedIds(new Set());
    fetchCurrentView(1, cache.searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutTypeFilter, layoutImageFilter]);

  // Clear selection when switching views
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, subView, libraryFilter]);

  // ── Tab handlers ──────────────────────────────────────────────────────────
  const handleTabChange = useCallback((key) => {
    if (key !== activeTab) setActiveTab(key);
  }, [activeTab]);

  const handleSubViewChange = useCallback((view) => {
    if (view !== subView) setSubView(view);
  }, [subView]);

  const handleLibraryFilterChange = useCallback((filter) => {
    if (filter !== libraryFilter) setLibraryFilter(filter);
  }, [libraryFilter]);

  // ── Search / Pagination ────────────────────────────────────────────────────
  const handleSearch = () => fetchCurrentView(1, cache.searchText);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleSearch(); }
  };

  const handleSearchInput = (e) => {
    const val = e.target.value;
    cache.searchText = val;
    forceRender();
    if (!val.trim()) fetchCurrentView(1, "");
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= cache.totalPages && page !== cache.currentPage) {
        fetchCurrentView(page, cache.searchText);
      }
    },
    [cache, fetchCurrentView]
  );

  // ── CRUD handlers ──────────────────────────────────────────────────────────
  const handleAssetAdded = () => {
    setShowAddDialog(false);
    setAddedTypes((prev) => ({ ...prev, [activeTab]: true }));
    fetchCurrentView(cache.currentPage, cache.searchText);
  };

  const handleClose = useCallback(() => {
    Object.keys(addedTypes).forEach((type) => {
      if (addedTypes[type]) {
        dispatch(refreshAssets(type));
      }
    });
    onClose();
  }, [addedTypes, dispatch, onClose]);

  // Replace the item IMMUTABLY. The grid is memoised on `assets`, so mutating
  // an item in place and bumping renderTick leaves the memo holding stale JSX —
  // the pill never flips, and the next click reads the mutated value and sends
  // the opposite of what the user expects. A new array identity is what makes
  // the memo recompute.
  const setAutoLayoutLocally = (id, value) => {
    const tc = getCache(currentCacheKey);
    tc.assets = tc.assets.map((a) => (a._id === id ? { ...a, auto_layout: value } : a));
    forceRender();
  };

  // Auto Layout flag. Ticking a layout MOVES it: it becomes available to
  // whole-book generation and stops coming up in the shuffle buttons, which
  // draw from the unticked complement. Optimistic — flip the cached item, then
  // revert if the request fails. Uses the dedicated updateAutoLayout endpoint:
  // updateLayout wants the whole payload and our layout_c here is DECOMPRESSED,
  // so sending the item back through it would corrupt the stored layout.
  const handleToggleAutoLayout = async (item) => {
    const next = !isAutoLayout(item);
    setAutoLayoutLocally(item._id, next);
    try {
      const res = await apiPost(ENDPOINTS.updateAutoLayout, {
        _id: item._id,
        auto_layout: next,
      });
      if (res?.status !== 1) throw new Error(res?.message || "Update failed");
      invalidateLayoutsCache(); // both editor pools must re-read
    } catch (err) {
      console.error("Error updating auto layout flag:", err);
      setAutoLayoutLocally(item._id, !next);
      alert("Could not update the Auto Layout setting. Please try again.");
    }
  };

  // Bulk Auto Layout — the pill is fine for a handful, useless for a catalog of
  // thousands. Reuses the multi-select the delete flow already has.
  const handleBulkAutoLayout = async (value) => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const res = await apiPost(ENDPOINTS.updateAutoLayoutMultiple, {
        ids: Array.from(selectedIds),
        auto_layout: value,
      });
      if (res?.status !== 1) throw new Error(res?.message || "Update failed");
      const tc = getCache(currentCacheKey);
      tc.assets = tc.assets.map((a) =>
        selectedIds.has(a._id) ? { ...a, auto_layout: value } : a
      );
      setSelectedIds(new Set());
      invalidateLayoutsCache();
      forceRender();
    } catch (err) {
      console.error("Error bulk updating auto layout flag:", err);
      alert("Could not update the selected layouts. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAssetClick = (id, e) => {
    e.stopPropagation();
    setDeleteConfirmDialog({ show: true, assetId: id, loading: false });
  };

  const handleConfirmDeleteAsset = async () => {
    if (!deleteConfirmDialog.assetId) return;
    setDeleteConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      // Layouts use their own delete endpoint; the base URL is currently the
      // same but this keeps the two swappable when the real API lands.
      const endpoint = activeTab === "layout"
        ? ENDPOINTS.deleteLayout
        : ENDPOINTS.deleteEditorSetting;
      const res = await apiDelete(endpoint + deleteConfirmDialog.assetId);
      if (res && (res.status === 1 || !res.error)) {
        if (activeTab === "layout") invalidateLayoutsCache();
        fetchCurrentView(cache.currentPage, cache.searchText);
        setDeleteConfirmDialog({ show: false, assetId: null, loading: false });
      } else {
        alert(res?.error || res?.message || "Failed to delete asset.");
        setDeleteConfirmDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting asset.");
      setDeleteConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };


  // Bulk delete. No multi-delete endpoint exists for layouts OR materials, so
  // loop the single DELETE per id in both cases.
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState({ show: false, loading: false });

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleteConfirm((prev) => ({ ...prev, loading: true }));
    const ids = Array.from(selectedIds);
    const endpoint = isLayoutTab ? ENDPOINTS.deleteLayout : ENDPOINTS.deleteEditorSetting;

    const failed = [];
    for (const id of ids) {
      try {
        const res = await apiDelete(endpoint + id);
        if (!(res && (res.status === 1 || !res.error))) failed.push(id);
      } catch (err) {
        console.error("Error deleting asset", id, err);
        failed.push(id);
      }
    }

    if (isLayoutTab) invalidateLayoutsCache();
    setSelectedIds(new Set());
    setBulkDeleteConfirm({ show: false, loading: false });
    fetchCurrentView(cache.currentPage, cache.searchText);
    if (failed.length) {
      alert(`Failed to delete ${failed.length} of ${ids.length} item(s).`);
    }
  };

  // ── Library multi-select actions ───────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    // Library (materials) view selects ALL for hide/enable. Delete views (layout
    // or materials Uploaded) select only the manageable (own) items.
    const selectable = isLibraryView ? assets : assets.filter(canManageItem);
    if (selectable.length > 0 && selectedIds.size === selectable.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map((a) => a._id)));
    }
  };

  const handleHideMaterials = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const res = await apiPost(ENDPOINTS.hideMaterialFromBrand, {
        type: "material",
        material_ids: Array.from(selectedIds),
        brand_id: [user?.brand_id],
      });
      if (res?.status === 1 || !res?.error) {
        setSelectedIds(new Set());
        // Invalidate both visible and hidden caches for this tab
        const visKey = getCacheKey(activeTab, "library", "visible");
        const hidKey = getCacheKey(activeTab, "library", "hidden");
        if (tabCacheRef.current[visKey]) tabCacheRef.current[visKey].loaded = false;
        if (tabCacheRef.current[hidKey]) tabCacheRef.current[hidKey].loaded = false;
        fetchCurrentView(1, cache.searchText);
      } else {
        alert(res?.error || "Failed to hide materials.");
      }
    } catch (err) {
      console.error(err);
      alert("Error hiding materials.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnableMaterials = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const res = await apiPost(ENDPOINTS.enableMaterialFromBrand, {
        type: "material",
        material_ids: Array.from(selectedIds),
        brand_id: [user?.brand_id],
      });
      if (res?.status === 1 || !res?.error) {
        setSelectedIds(new Set());
        const visKey = getCacheKey(activeTab, "library", "visible");
        const hidKey = getCacheKey(activeTab, "library", "hidden");
        if (tabCacheRef.current[visKey]) tabCacheRef.current[visKey].loaded = false;
        if (tabCacheRef.current[hidKey]) tabCacheRef.current[hidKey].loaded = false;
        fetchCurrentView(1, cache.searchText);
      } else {
        alert(res?.error || "Failed to enable materials.");
      }
    } catch (err) {
      console.error(err);
      alert("Error enabling materials.");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const { assets, currentPage, totalPages, totalCount, searchText } = cache;
  const isLibraryView = isAdmin && subView === "library";
  // How many items on this page can be multi-selected for DELETE. Layouts:
  // always. Materials: only in a manage/uploaded view (the Library view is for
  // hide/enable, not delete). Gated to items the user owns (own brand / super).
  const deletableCount = useMemo(
    () => ((isLayoutTab || !isLibraryView) ? assets.filter(canManageItem).length : 0),
    [isLayoutTab, isLibraryView, assets, canManageItem]
  );

  // ── Render grid ────────────────────────────────────────────────────────────
  const renderedAssetsGrid = useMemo(() => {
    return (
      <AssetsGrid>
        {assets.map((item) => {
          // Layouts → SVG preview card with multi-select checkbox + delete
          // (only for layouts the current user owns / can manage).
          if (isLayoutTab) {
            const canDelete = canManageItem(item);
            const isSelected = selectedIds.has(item._id);
            return (
              <AssetCard key={item._id} $selected={canDelete && isSelected}>
                {canDelete && (
                  <>
                    <SelectCheckbox
                      $checked={isSelected}
                      style={{ left: 8, right: "auto" }}
                      onClick={(e) => { e.stopPropagation(); toggleSelect(item._id); }}
                    >
                      {isSelected && <FiCheck size={12} />}
                    </SelectCheckbox>
                    <DeleteBtn
                      title="Delete Layout"
                      onClick={(e) => handleDeleteAssetClick(item._id, e)}
                    >
                      <FiTrash2 size={14} />
                    </DeleteBtn>
                  </>
                )}
                <LayoutPreview>
                  {renderLayoutPreview(item) || (
                    <NoImagePlaceholder><FiImage size={28} /></NoImagePlaceholder>
                  )}
                </LayoutPreview>
                {item.name && <AssetName title={item.name}>{item.name}</AssetName>}
                {canDelete && (
                  <AutoLayoutToggle
                    $on={isAutoLayout(item)}
                    title={
                      isAutoLayout(item)
                        ? "Reserved for Auto Layout — untick to return it to the Shuffle pool"
                        : "Not in Auto Layout — tick to reserve it (it leaves the Shuffle pool)"
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isAutoLayout(item)}
                      onChange={() => handleToggleAutoLayout(item)}
                    />
                    Auto Layout
                  </AutoLayoutToggle>
                )}
              </AssetCard>
            );
          }

          const thumb = getAssetThumbnail(item);

          // Library view → selectable cards, no edit/delete
          if (isLibraryView) {
            const isSelected = selectedIds.has(item._id);
            return (
              <AssetCard
                key={item._id}
                $selectable
                $selected={isSelected}
                onClick={() => toggleSelect(item._id)}
              >
                <SelectCheckbox $checked={isSelected}>
                  {isSelected && <FiCheck size={12} />}
                </SelectCheckbox>
                {thumb ? (
                  <img src={thumb} alt={item.name || "Asset"} loading="lazy" decoding="async" draggable={false} style={{ contentVisibility: "auto" }} />
                ) : (
                  <NoImagePlaceholder><FiImage size={28} /></NoImagePlaceholder>
                )}
                {item.name && <AssetName title={item.name}>{item.name}</AssetName>}
              </AssetCard>
            );
          }

          // Uploaded view — existing behavior + multi-select checkbox
          const canViewAndEdit =
            isSuperUser ||
            (isAdmin && item.brand_id && item.brand_id === user?.brand_id);
          const isSelected = selectedIds.has(item._id);

          return (
            <AssetCard key={item._id} $selected={canViewAndEdit && isSelected}>
              {canViewAndEdit && (
                <>
                  <SelectCheckbox
                    $checked={isSelected}
                    style={{ left: 8, right: "auto" }}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(item._id); }}
                  >
                    {isSelected && <FiCheck size={12} />}
                  </SelectCheckbox>
                  <EditBtn
                    title="Edit Asset"
                    onClick={(e) => { e.stopPropagation(); setViewAssetId(item._id); }}
                  >
                    <FiEdit2 size={13} />
                  </EditBtn>
                  <DeleteBtn
                    title="Delete Asset"
                    onClick={(e) => handleDeleteAssetClick(item._id, e)}
                  >
                    <FiTrash2 size={14} />
                  </DeleteBtn>
                </>
              )}
              {thumb ? (
                <img src={thumb} alt={item.name || "Asset"} loading="lazy" decoding="async" draggable={false} style={{ contentVisibility: "auto" }} />
              ) : (
                <NoImagePlaceholder><FiImage size={28} /></NoImagePlaceholder>
              )}
              {item.name && <AssetName title={item.name}>{item.name}</AssetName>}
            </AssetCard>
          );
        })}
      </AssetsGrid>
    );
  }, [assets, isLibraryView, isLayoutTab, canManageItem, selectedIds, isSuperUser, isAdmin, user?.brand_id]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <DialogOverlay>
        <DialogContainer onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <DialogHeader>
            <DialogTitle>
              <h2>Asset Management</h2>
              <p>Manage backgrounds, cliparts, and masks for the editor</p>
            </DialogTitle>
            <CloseButton onClick={handleClose} aria-label="Close">
              <IoClose size={22} />
            </CloseButton>
          </DialogHeader>

{/* Main Tabs (Background / Clipart / Mask / Lay          outs).
              Layouts is SUPER-USER ONLY — hidden for store managers/admins. */}
          <TabBar>
            {ASSET_TABS.filter((tab) => tab.key !== "layout" || isSuperUser).map((tab) => (
              <TabBtn
                key={tab.key}
                $active={activeTab === tab.key}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </TabBtn>
            ))}
          </TabBar>

          {/* Sub-controls: Uploaded / Library + Visible / Hidden (Admin only).
              Hidden for Layouts: apiPost force-injects filter.brand_id, so a
              layout query is ALWAYS scoped to the caller's brand (super-admin →
              global "", store manager → their brand). "Uploaded" vs "Library"
              would return the identical set, so the toggle is meaningless. */}
          {isAdmin && !isLayoutTab && (
            <SubControlRow>
              <SegmentedControl>
                <SegmentBtn $active={subView === "uploaded"} onClick={() => handleSubViewChange("uploaded")}>
                  Uploaded
                </SegmentBtn>
                <SegmentBtn $active={subView === "library"} onClick={() => handleSubViewChange("library")}>
                  Library
                </SegmentBtn>
              </SegmentedControl>

              {subView === "library" && !isLayoutTab && (
                <>
                  <Divider />
                  <ToggleGroup>
                    <ToggleBtn $active={libraryFilter === "visible"} onClick={() => handleLibraryFilterChange("visible")}>
                      <FiEye size={13} /> Visible
                    </ToggleBtn>
                    <ToggleBtn $active={libraryFilter === "hidden"} $variant="hidden" onClick={() => handleLibraryFilterChange("hidden")}>
                      <FiEyeOff size={13} /> Hidden
                    </ToggleBtn>
                  </ToggleGroup>
                </>
              )}
            </SubControlRow>
          )}

          {/* Toolbar */}
          <ToolbarRow>
            <SearchWrapper>
              <FiSearch size={16} />
              <input
                type="text"
                placeholder={`Search ${activeTabConfig?.label?.toLowerCase() || "assets"}...`}
                value={searchText}
                onChange={handleSearchInput}
                onKeyDown={handleSearchKeyDown}
                aria-label="Search assets"
              />
            </SearchWrapper>

            {/* Layout filters — type (page/spread) + exact photo count */}
            {isLayoutTab && (
              <>
                <FilterSelect
                  value={layoutTypeFilter}
                  onChange={(e) => setLayoutTypeFilter(e.target.value)}
                  title="Filter by layout type"
                  aria-label="Filter by layout type"
                >
                  <option value="all">All types</option>
                  <option value="single">Page (single)</option>
                  <option value="spread">Spread</option>
                </FilterSelect>
                <FilterSelect
                  value={layoutImageFilter}
                  onChange={(e) => setLayoutImageFilter(e.target.value)}
                  title="Filter by number of photos"
                  aria-label="Filter by number of photos"
                >
                  <option value="all">Any photos</option>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n} photo{n > 1 ? "s" : ""}</option>
                  ))}
                </FilterSelect>
              </>
            )}

            {/* Add button only in Uploaded view (layouts are imported elsewhere) */}
            {!isLayoutTab && (!isAdmin || subView === "uploaded") && (
              <AddButton onClick={() => setShowAddDialog(true)}>
                <FiPlus size={16} />
                Add {activeTabConfig?.singular}
              </AddButton>
            )}

            {/* Multi-select + bulk delete — layouts AND materials (Uploaded/
                manage view only; the Library view below is hide/enable). */}
            {(isLayoutTab || !isLibraryView) && deletableCount > 0 && (
              <>
                <ActionButton onClick={handleSelectAll} style={{ background: "#6b7280" }}>
                  {selectedIds.size === deletableCount ? "Deselect All" : "Select All"}
                </ActionButton>
                {selectedIds.size > 0 && isLayoutTab && (
                  <>
                    <ActionButton
                      onClick={() => handleBulkAutoLayout(true)}
                      disabled={actionLoading}
                      title="Reserve the selected layouts for Auto Layout"
                    >
                      {actionLoading ? <Spinner animation="border" size="sm" /> : <FiCheck size={14} />}
                      Auto Layout ({selectedIds.size})
                    </ActionButton>
                    <ActionButton
                      onClick={() => handleBulkAutoLayout(false)}
                      disabled={actionLoading}
                      style={{ background: "#6b7280" }}
                      title="Return the selected layouts to the Shuffle pool"
                    >
                      {actionLoading ? <Spinner animation="border" size="sm" /> : <FiX size={14} />}
                      Shuffle ({selectedIds.size})
                    </ActionButton>
                  </>
                )}
                {selectedIds.size > 0 && (
                  <ActionButton
                    $variant="danger"
                    onClick={() => setBulkDeleteConfirm({ show: true, loading: false })}
                  >
                    <FiTrash2 size={14} /> Delete ({selectedIds.size})
                  </ActionButton>
                )}
              </>
            )}

            {/* Library multi-select actions (materials only, not layouts) */}
            {isLibraryView && !isLayoutTab && (
              <>
                <ActionButton
                  onClick={handleSelectAll}
                  style={{ background: "#6b7280" }}
                >
                  {selectedIds.size === assets.length && assets.length > 0 ? "Deselect All" : "Select All"}
                </ActionButton>

                {selectedIds.size > 0 && (
                  libraryFilter === "visible" ? (
                    <ActionButton
                      $variant="danger"
                      onClick={handleHideMaterials}
                      disabled={actionLoading}
                    >
                      {actionLoading ? <Spinner animation="border" size="sm" /> : <FiEyeOff size={14} />}
                      Hide ({selectedIds.size})
                    </ActionButton>
                  ) : (
                    <ActionButton onClick={handleEnableMaterials} disabled={actionLoading}>
                      {actionLoading ? <Spinner animation="border" size="sm" /> : <FiEye size={14} />}
                      Show ({selectedIds.size})
                    </ActionButton>
                  )
                )}
              </>
            )}
          </ToolbarRow>

          {/* Body */}
          <DialogBody>
            {isLoading ? (
              <LoadingWrapper><Spinner animation="border" size="sm" /></LoadingWrapper>
            ) : assets.length === 0 ? (
              <EmptyState>
                <div className="icon-wrapper"><FiImage size={22} /></div>
                <h3>No {activeTabConfig?.label?.toLowerCase()} found</h3>
                <p>
                  {totalCount === 0 && !searchText.trim()
                    ? isLibraryView
                      ? `No ${libraryFilter} ${activeTabConfig?.label?.toLowerCase()} in the library`
                      : isLayoutTab
                        ? "No layouts found for this store"
                        : `Add your first ${activeTabConfig?.singular?.toLowerCase()} to get started`
                    : "No results match your current search"}
                </p>
                {totalCount === 0 && !searchText.trim() && !isLibraryView && !isLayoutTab && (
                  <AddButton onClick={() => setShowAddDialog(true)}>
                    <FiPlus size={16} />
                    Add {activeTabConfig?.singular}
                  </AddButton>
                )}
              </EmptyState>
            ) : (
              renderedAssetsGrid
            )}
          </DialogBody>

          {/* Footer */}
          <FooterRow>
            <FooterInfo>
              {totalCount > 0
                ? `Showing ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalCount)}–${Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of ${totalCount} ${activeTabConfig?.label?.toLowerCase()}`
                : `0 ${activeTabConfig?.label?.toLowerCase()}`}
            </FooterInfo>
            {totalPages > 1 && (
              <PaginationWrapper>
                <Pagination>
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                  {currentPage - 10 > 0 && (
                    <>
                      <Pagination.Item onClick={() => handlePageChange(currentPage - 10)}>{currentPage - 10}</Pagination.Item>
                      <Pagination.Ellipsis />
                    </>
                  )}
                  {currentPage > 1 && totalPages < 10 && (
                    <Pagination.Item onClick={() => handlePageChange(currentPage - 1)}>{currentPage - 1}</Pagination.Item>
                  )}
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {currentPage < totalPages && totalPages < 10 && (
                    <Pagination.Item onClick={() => handlePageChange(currentPage + 1)}>{currentPage + 1}</Pagination.Item>
                  )}
                  {currentPage + 10 <= totalPages && (
                    <>
                      <Pagination.Ellipsis />
                      <Pagination.Item onClick={() => handlePageChange(currentPage + 10)}>{currentPage + 10}</Pagination.Item>
                    </>
                  )}
                  <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                </Pagination>
              </PaginationWrapper>
            )}
          </FooterRow>
        </DialogContainer>
      </DialogOverlay>

      {/* Confirmation Dialog for Asset Deletion */}
      <ConfirmationDialog
        show={deleteConfirmDialog.show}
        onClose={() => setDeleteConfirmDialog({ show: false, assetId: null, loading: false })}
        onConfirm={handleConfirmDeleteAsset}
        title={isLayoutTab ? "Delete Layout" : "Delete Asset"}
        message={`Are you sure you want to permanently delete this ${isLayoutTab ? "layout" : "asset"}? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleteConfirmDialog.loading}
      />

      {/* Confirmation Dialog for Bulk Layout Deletion */}
      <ConfirmationDialog
        show={bulkDeleteConfirm.show}
        onClose={() => setBulkDeleteConfirm({ show: false, loading: false })}
        onConfirm={handleConfirmBulkDelete}
        title={isLayoutTab ? "Delete Layouts" : `Delete ${activeTabConfig?.label || "Assets"}`}
        message={`Are you sure you want to permanently delete ${selectedIds.size} ${isLayoutTab ? "layout" : (activeTabConfig?.singular?.toLowerCase() || "asset")}(s)? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={bulkDeleteConfirm.loading}
      />

      {/* Add Dialog (only in Uploaded view) */}
      {showAddDialog && (
        <AddAssetDialog
          isOpen={showAddDialog}
          assetType={activeTab}
          onClose={() => setShowAddDialog(false)}
          onSuccess={handleAssetAdded}
        />
      )}

      {/* View/Edit Dialog */}
      {viewAssetId && (
        <ViewAssetDialog
          isOpen={true}
          assetId={viewAssetId}
          onClose={() => setViewAssetId(null)}
          onSuccess={handleAssetAdded}
        />
      )}
    </>,
    document.body
  );
}

export default AssetManagementDialog;
